import { useEffect, useState } from 'react'
import { RouterProvider } from 'react-router-dom'

import { API } from '@/api'
import { DomainProvider } from '@/contexts/DomainContext'

import { PageLoading } from './components/PageLoading'
import { router } from './router'
import { useAccountsStore } from './stores/accounts'
import { useTeamStore } from './stores/team'
import { useUserStore } from './stores/user'
import { markContextSyncNavigation, subscribeContextSync } from './utils/contextSync'

export default function App() {
  const [loading, setLoading] = useState(true)

  const userId = useUserStore((state) => state.id)
  const getAccount = useUserStore((state) => state.getAccount)

  useEffect(() => {
    // Only poll account data when user is logged in
    if (userId <= 0) {
      return
    }

    const intervalId = setInterval(() => {
      getAccount()
    }, 5000)

    return () => clearInterval(intervalId)
  }, [getAccount, userId])

  useEffect(() => {
    let lastHandledSyncEvent = 0
    const unsubscribe = subscribeContextSync((message) => {
      if (message.timestamp <= lastHandledSyncEvent) {
        return
      }

      lastHandledSyncEvent = message.timestamp

      if (message.type === 'TEAM_CONTEXT_SWITCHED') {
        const targetPath = message.targetPath || '/team/home'
        const currentPath = window.location.pathname
        const currentTeamId = useTeamStore.getState().teamTokenData?.teamId
        const hasDifferentTeamId =
          typeof message.teamId === 'number' &&
          typeof currentTeamId === 'number' &&
          message.teamId !== currentTeamId
        const shouldForceReload = message.forceReload === true

        if (currentPath !== targetPath) {
          markContextSyncNavigation()
          window.location.href = targetPath
        } else if (hasDifferentTeamId || shouldForceReload) {
          markContextSyncNavigation()
          window.location.reload()
        }
        return
      }

      if (message.type === 'PLATFORM_CONTEXT_SWITCHED') {
        useTeamStore.getState().clearTeamContext()
        const targetPath = message.targetPath || '/create'
        if (window.location.pathname !== targetPath) {
          markContextSyncNavigation()
          window.location.href = targetPath
        }
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    // Initialize auth state from cookies (single source of truth initialization)
    useTeamStore.getState().initializeAuthState()

    // Initialize team token refresh mechanisms (timer + visibility listener)
    import('@/utils/teamTokenScheduler').then(({ initializeTeamTokenRefresh }) => {
      initializeTeamTokenRefresh()
    })

    // Only initialize once, avoid re-running if user is already logged in
    const currentUser = useUserStore.getState()
    if (currentUser.id > 0) {
      // User already loaded, just initialize accounts store if needed
      useAccountsStore
        .getState()
        .initialize({
          id: currentUser.id,
          name: currentUser.name,
          email: currentUser.email,
        })
        .finally(() => {
          setLoading(false)
        })
      return
    }

    // Initial load - fetch user info
    Promise.all([
      useUserStore.getState().getUserInfo(),
      useUserStore.getState().getAccount(),
      // Load private region permission (fail silently, default to false)
      API.getPrivateRegionPermission()
        .then((res) => {
          if (res.code === 0) {
            useUserStore.getState().setPrivateRegionPermission(res.hasPermission)
          }
        })
        .catch(() => {
          // Keep default value (false) on error
        }),
    ])
      .then(() => {
        const userState = useUserStore.getState()
        if (userState.id > 0) {
          return useAccountsStore.getState().initialize({
            id: userState.id,
            name: userState.name,
            email: userState.email,
          })
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  return (
    <DomainProvider>
      {loading ? <PageLoading /> : <RouterProvider router={router} />}
    </DomainProvider>
  )
}
